import React, { useState, useRef, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import EmailIcon from '@mui/icons-material/Email'

interface TermsModalProps {
  open: boolean
  onClose: () => void
  onAgree: (sendEmail: boolean) => void
}

export const TermsModal: React.FC<TermsModalProps> = ({ open, onClose, onAgree }) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
  const [sendEmailChecked, setSendEmailChecked] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))

  const handleScroll = useCallback(() => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current
      // スクロール位置が下端から50px以内なら最後まで読んだとみなす
      if (scrollHeight - scrollTop - clientHeight < 50) {
        setHasScrolledToBottom(true)
      }
    }
  }, [])

  const handleClose = () => {
    setHasScrolledToBottom(false)
    setSendEmailChecked(false)
    onClose()
  }

  const handleAgree = () => {
    const shouldSendEmail = sendEmailChecked
    setHasScrolledToBottom(false)
    setSendEmailChecked(false)
    onAgree(shouldSendEmail)
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      aria-labelledby="terms-dialog-title"
    >
      <DialogTitle
        id="terms-dialog-title"
        sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography variant="h6" component="span" fontWeight="bold">
          利用規約
        </Typography>
        <IconButton aria-label="閉じる" onClick={handleClose} sx={{ color: 'grey.500' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        ref={contentRef}
        onScroll={handleScroll}
        sx={{
          height: fullScreen ? 'auto' : '60vh',
          overflowY: 'auto',
        }}
      >
        <Box sx={{ px: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            ComplianceAuto サービス利用規約
            <br />
            最終更新日: 2025年12月11日
          </Typography>

          <Section title="第1条（適用）">
            <Typography variant="body2" paragraph>
              本規約は、GARAPON STUDIO
              LLC（以下「当社」といいます）が提供するComplianceAuto（以下「本サービス」といいます）の利用に関する条件を、本サービスを利用するお客様（以下「利用者」といいます）と当社との間で定めるものです。
            </Typography>
            <Typography variant="body2" paragraph>
              利用者は、本サービスを利用することにより、本規約のすべての条項に同意したものとみなされます。
            </Typography>
          </Section>

          <Section title="第2条（定義）">
            <Typography variant="body2" paragraph>
              本規約において使用する用語の定義は、以下のとおりとします。
            </Typography>
            <Box component="ol" sx={{ pl: 2, '& li': { mb: 1 } }}>
              <Typography component="li" variant="body2">
                <strong>「本サービス」</strong>
                とは、当社が提供するAIを活用したコンプライアンス教育自動化システム「ComplianceAuto」およびこれに付随するすべてのサービスを意味します。
              </Typography>
              <Typography component="li" variant="body2">
                <strong>「利用者」</strong>
                とは、本規約に同意のうえ、当社の定める手続きに従って本サービスの利用登録を完了した法人または個人を意味します。
              </Typography>
              <Typography component="li" variant="body2">
                <strong>「テナント」</strong>
                とは、本サービス上で利用者ごとに独立して管理される企業単位のデータ領域を意味します。
              </Typography>
              <Typography component="li" variant="body2">
                <strong>「コンテンツ」</strong>
                とは、利用者が本サービス上で作成、入力、登録、またはアップロードする情報（学習教材、理解度テスト問題、学習データ等）を意味します。
              </Typography>
            </Box>
          </Section>

          <Section title="第3条（利用登録）">
            <Typography variant="body2" paragraph>
              本サービスの利用を希望する者は、当社所定の登録フォームに必要事項を入力し、送信することにより利用登録を申請するものとします。
            </Typography>
            <Typography variant="body2" paragraph>
              当社は、以下の場合には利用登録を承認しないことがあります。
            </Typography>
            <Box component="ol" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
              <Typography component="li" variant="body2">
                登録申請者が過去に本規約に違反したことがある場合
              </Typography>
              <Typography component="li" variant="body2">
                登録申請に虚偽の内容が含まれている場合
              </Typography>
              <Typography component="li" variant="body2">
                その他、当社が利用登録を適当でないと判断した場合
              </Typography>
            </Box>
          </Section>

          <Section title="第4条（アカウント管理）">
            <Typography variant="body2" paragraph>
              利用者は、自己の責任において、本サービスのアカウント情報（メールアドレスおよびパスワード）を適切に管理・保管するものとします。
            </Typography>
            <Typography variant="body2" paragraph>
              利用者は、アカウント情報を第三者に利用させ、または貸与、譲渡、名義変更、売買等をしてはなりません。
            </Typography>
            <Typography variant="body2" paragraph>
              アカウント情報の管理不十分、使用上の過誤、第三者の使用等による損害の責任は利用者が負うものとし、当社は一切の責任を負いません。
            </Typography>
          </Section>

          <Section title="第5条（利用料金）">
            <Typography variant="body2" paragraph>
              本サービスの利用料金は、当社が別途定める料金体系に従うものとします。
            </Typography>
            <Typography variant="body2" paragraph>
              利用者は、当社が定める支払方法により、利用料金を支払うものとします。
            </Typography>
            <HighlightBox>
              支払い済みの利用料金は、法令で定められた場合を除き、返金いたしません。
            </HighlightBox>
          </Section>

          <Section title="第6条（禁止事項）">
            <Typography variant="body2" paragraph>
              利用者は、本サービスの利用にあたり、以下の行為をしてはなりません。
            </Typography>
            <Box component="ol" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
              <Typography component="li" variant="body2">
                法令または公序良俗に違反する行為
              </Typography>
              <Typography component="li" variant="body2">
                犯罪行為に関連する行為
              </Typography>
              <Typography component="li" variant="body2">
                当社、他の利用者、または第三者の知的財産権、肖像権、プライバシー、名誉その他の権利または利益を侵害する行為
              </Typography>
              <Typography component="li" variant="body2">
                本サービスのサーバーまたはネットワークに過度な負荷をかける行為
              </Typography>
              <Typography component="li" variant="body2">
                本サービスの運営を妨害するおそれのある行為
              </Typography>
              <Typography component="li" variant="body2">
                不正アクセスをし、またはこれを試みる行為
              </Typography>
              <Typography component="li" variant="body2">
                他の利用者に関する個人情報等を収集または蓄積する行為
              </Typography>
              <Typography component="li" variant="body2">
                他の利用者に成りすます行為
              </Typography>
              <Typography component="li" variant="body2">
                本サービスに関連して、反社会的勢力に対して直接または間接に利益を供与する行為
              </Typography>
              <Typography component="li" variant="body2">
                その他、当社が不適切と判断する行為
              </Typography>
            </Box>
          </Section>

          <Section title="第7条（本サービスの提供の停止等）">
            <Typography variant="body2" paragraph>
              当社は、以下のいずれかの事由があると判断した場合、利用者に事前に通知することなく本サービスの全部または一部の提供を停止または中断することができるものとします。
            </Typography>
            <Box component="ol" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
              <Typography component="li" variant="body2">
                本サービスにかかるシステムの保守点検または更新を行う場合
              </Typography>
              <Typography component="li" variant="body2">
                地震、落雷、火災、停電、天災またはウィルスの蔓延などの不可抗力により、本サービスの提供が困難となった場合
              </Typography>
              <Typography component="li" variant="body2">
                システムまたは通信回線等が事故により停止した場合
              </Typography>
              <Typography component="li" variant="body2">
                その他、当社が本サービスの提供が困難と判断した場合
              </Typography>
            </Box>
          </Section>

          <Section title="第8条（知的財産権）">
            <Typography variant="body2" paragraph>
              本サービスおよび本サービスに関連するコンテンツ（当社が作成したもの）に関する知的財産権は、すべて当社または当社にライセンスを許諾している者に帰属しており、本規約に基づく本サービスの利用許諾は、本サービスに関する当社または当社にライセンスを許諾している者の知的財産権の使用許諾を意味するものではありません。
            </Typography>
            <Typography variant="body2" paragraph>
              利用者が本サービス上で作成したコンテンツの知的財産権は、利用者に帰属します。ただし、当社は、本サービスの運営・改善のために必要な範囲で、利用者のコンテンツを利用できるものとします。
            </Typography>
          </Section>

          <Section title="第9条（AI生成コンテンツ）">
            <Typography variant="body2" paragraph>
              本サービスでは、OpenAI社のAPIを活用したAI機能を提供しています。AI機能により生成されたコンテンツについて、当社はその正確性、完全性、適法性を保証するものではありません。
            </Typography>
            <Typography variant="body2" paragraph>
              利用者は、AI生成コンテンツを利用する際は、自己の責任において内容を確認し、適切に利用するものとします。
            </Typography>
          </Section>

          <Section title="第10条（データの取り扱い）">
            <Typography variant="body2" paragraph>
              当社は、利用者のデータを以下のとおり取り扱います。
            </Typography>
            <Box component="ol" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
              <Typography component="li" variant="body2">
                <strong>データの分離</strong>:
                各テナントのデータは完全に分離して保管され、他のテナントからアクセスすることはできません。
              </Typography>
              <Typography component="li" variant="body2">
                <strong>データの保護</strong>:
                当社は、適切な技術的・組織的措置を講じて、利用者のデータを保護します。
              </Typography>
              <Typography component="li" variant="body2">
                <strong>データの削除</strong>:
                利用者がサービスを解約した場合、当社は合理的な期間内に利用者のデータを削除します。
              </Typography>
            </Box>
            <Typography variant="body2" paragraph>
              個人情報の取り扱いについては、別途定める「プライバシーポリシー」に従います。
            </Typography>
          </Section>

          <Section title="第11条（退会）">
            <Typography variant="body2" paragraph>
              利用者は、当社所定の手続きにより、本サービスから退会することができます。
            </Typography>
            <Typography variant="body2" paragraph>
              退会後も、利用者の本規約上の義務および当社の免責事項は、引き続き効力を有するものとします。
            </Typography>
          </Section>

          <Section title="第12条（免責事項）">
            <Typography variant="body2" paragraph>
              当社は、本サービスに関して、以下の事項について一切の責任を負いません。
            </Typography>
            <Box component="ol" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
              <Typography component="li" variant="body2">
                本サービスが利用者の特定の目的に適合すること
              </Typography>
              <Typography component="li" variant="body2">
                本サービスの正確性、完全性、継続性、適時性
              </Typography>
              <Typography component="li" variant="body2">
                本サービスの利用により利用者に生じた損害
              </Typography>
              <Typography component="li" variant="body2">
                利用者と他の利用者または第三者との間において生じた取引、連絡または紛争
              </Typography>
            </Box>
            <HighlightBox>
              当社が利用者に対して負う損害賠償責任の範囲は、当社の故意または重大な過失による場合を除き、利用者が過去12ヶ月間に当社に支払った利用料金の総額を上限とします。
            </HighlightBox>
          </Section>

          <Section title="第13条（サービス内容の変更等）">
            <Typography variant="body2" paragraph>
              当社は、利用者への事前の告知なく、本サービスの内容を変更し、または本サービスの提供を中止することができるものとし、これによって利用者に生じた損害について一切の責任を負いません。
            </Typography>
          </Section>

          <Section title="第14条（利用規約の変更）">
            <Typography variant="body2" paragraph>
              当社は、必要と判断した場合には、利用者への事前の告知なく本規約を変更することができるものとします。
            </Typography>
            <Typography variant="body2" paragraph>
              変更後の利用規約は、当社ウェブサイト上に掲載された時点から効力を生じるものとし、利用者が変更後に本サービスを利用した場合、変更後の規約に同意したものとみなします。
            </Typography>
          </Section>

          <Section title="第15条（通知または連絡）">
            <Typography variant="body2" paragraph>
              利用者と当社との間の通知または連絡は、当社の定める方法によって行うものとします。当社は、利用者から当社所定の方式により変更届出がない限り、登録されている連絡先が有効なものとみなして通知または連絡を行い、これらは発信時に利用者に到達したものとみなします。
            </Typography>
          </Section>

          <Section title="第16条（権利義務の譲渡の禁止）">
            <Typography variant="body2" paragraph>
              利用者は、当社の書面による事前の承諾なく、本規約に基づく権利または義務を第三者に譲渡し、または担保に供することはできません。
            </Typography>
          </Section>

          <Section title="第17条（準拠法・裁判管轄）">
            <Typography variant="body2" paragraph>
              本規約の解釈にあたっては、日本法を準拠法とします。
            </Typography>
            <Typography variant="body2" paragraph>
              本サービスに関して紛争が生じた場合には、当社の本店所在地を管轄する裁判所を専属的合意管轄裁判所とします。
            </Typography>
          </Section>

          <Section title="お問い合わせ" isLast>
            <Typography variant="body2" paragraph>
              本規約に関するお問い合わせは、以下の窓口までご連絡ください。
            </Typography>
            <HighlightBox>
              <strong>GARAPON STUDIO LLC</strong>
              <br />
              お問い合わせフォーム:{' '}
              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLSdK4sTdjW6cCpf9dSQrrMhuFBQUKMJieMhUrgae9S18Z-skMw/viewform?usp=dialog"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#667eea' }}
              >
                お問い合わせ
              </a>
            </HighlightBox>
          </Section>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, flexDirection: 'column', gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
          {hasScrolledToBottom
            ? '利用規約を確認しました。同意する場合は下のボタンをクリックしてください。'
            : '利用規約を最後までスクロールしてお読みください。'}
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={sendEmailChecked}
              onChange={(e) => setSendEmailChecked(e.target.checked)}
              size="small"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EmailIcon fontSize="small" color="action" />
              <Typography variant="body2">ログイン後、利用規約のURLをメールで送信する</Typography>
            </Box>
          }
          sx={{ m: 0 }}
        />

        <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={handleClose} color="inherit">
            閉じる
          </Button>
          <Button onClick={handleAgree} variant="contained" disabled={!hasScrolledToBottom}>
            同意する
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

// セクションコンポーネント
interface SectionProps {
  title: string
  children: React.ReactNode
  isLast?: boolean
}

const Section: React.FC<SectionProps> = ({ title, children, isLast }) => (
  <Box sx={{ mb: isLast ? 0 : 3 }}>
    <Typography
      variant="subtitle1"
      fontWeight="bold"
      sx={{
        mb: 1,
        pb: 0.5,
        borderBottom: '2px solid',
        borderColor: 'primary.main',
        display: 'inline-block',
      }}
    >
      {title}
    </Typography>
    {children}
    {!isLast && <Divider sx={{ mt: 2 }} />}
  </Box>
)

// ハイライトボックスコンポーネント
interface HighlightBoxProps {
  children: React.ReactNode
}

const HighlightBox: React.FC<HighlightBoxProps> = ({ children }) => (
  <Box
    sx={{
      bgcolor: 'grey.50',
      borderLeft: '4px solid',
      borderColor: 'primary.main',
      p: 2,
      my: 1,
      borderRadius: '0 8px 8px 0',
    }}
  >
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Box>
)
